import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Scissors, Calendar as CalendarIcon, User, MapPin, Clock, RotateCcw, Check, Trash2, Edit3 } from 'lucide-react';
import { useSmartTranslation } from '../../hooks/useStudentLabel';
import TreatmentStatusBadge from '../TransplantCrm/TreatmentStatusBadge';

const KNOWN_TYPE_COLORS = {
  FUE: '#6366f1',
  FUT: '#0ea5e9',
  DHI: '#8b5cf6',
  PRP: '#10b981',
  consultation: '#f59e0b',
};

function hashColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 55%, 50%)`;
}

function getTypeColor(type) {
  if (!type) return '#7C3AED';
  if (KNOWN_TYPE_COLORS[type]) return KNOWN_TYPE_COLORS[type];
  const lower = type.toLowerCase();
  for (const [k, v] of Object.entries(KNOWN_TYPE_COLORS)) {
    if (lower === k.toLowerCase()) return v;
  }
  return hashColor(type);
}

function hexToRgba(hex, alpha) {
  if (hex.startsWith('hsl')) {
    return hex.replace('hsl(', 'hsla(').replace(')', `, ${alpha})`);
  }
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default function UpcomingTreatmentsTable({
  treatments = [],
  treatmentTypes = [],
  clientMap = {},
  workerMap = {},
  isRTL = false,
  isMobile = false,
  isTablet = false,
  onRowClick,
  onRemove,
  onUpdateStatus,
}) {
  const { i18n } = useTranslation();
  const t = useSmartTranslation();
  const [selectedTypes, setSelectedTypes] = useState(() => new Set());
  // Default range: 'next3' until we see real data, then auto-pick 'today' if
  // today already has 5+ treatments (else 'next3' so the dashboard isn't empty).
  const [dateRange, setDateRange] = useState('next3');
  const userPickedRange = useRef(false);
  const autoDefaultApplied = useRef(false);

  // Auto-pick the initial default once treatments first arrive. Re-runs only
  // until the user clicks a pill (then their choice sticks).
  useEffect(() => {
    if (userPickedRange.current || autoDefaultApplied.current) return;
    if (!treatments || treatments.length === 0) return;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000 - 1);
    const todayCount = treatments.reduce((n, m) => {
      if (!m.date) return n;
      const d = new Date(m.date);
      if (Number.isNaN(d.getTime())) return n;
      return d >= todayStart && d <= todayEnd ? n + 1 : n;
    }, 0);
    setDateRange(todayCount >= 5 ? 'today' : 'next3');
    autoDefaultApplied.current = true;
  }, [treatments]);

  const handleRangeChange = useCallback((key) => {
    userPickedRange.current = true;
    setDateRange(key);
  }, []);

  const scrollRef = useRef(null);
  const [showStartFade, setShowStartFade] = useState(false);
  const [showEndFade, setShowEndFade] = useState(false);

  const updateFades = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const effective = isRTL ? -scrollLeft : scrollLeft;
    setShowStartFade(Math.abs(effective) > 8);
    setShowEndFade(Math.abs(effective) + clientWidth < scrollWidth - 8);
  }, [isRTL]);

  useEffect(() => { updateFades(); }, [updateFades, treatmentTypes]);

  const dateRangeBounds = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const days = dateRange === 'today' ? 1 : dateRange === 'next3' ? 3 : 7;
    const end = new Date(start.getTime() + days * 24 * 60 * 60 * 1000 - 1);
    return { start, end };
  }, [dateRange]);

  const dateFiltered = useMemo(() => {
    return treatments
      .filter((m) => {
        if (!m.date) return false;
        const d = new Date(m.date);
        if (Number.isNaN(d.getTime())) return false;
        return d >= dateRangeBounds.start && d <= dateRangeBounds.end;
      })
      .slice()
      .sort((a, b) => {
        // Sort by date ascending, then by time ascending within the same day.
        const da = String(a.date || '');
        const db = String(b.date || '');
        if (da !== db) return da < db ? -1 : 1;
        const ta = String(a.time || '');
        const tb = String(b.time || '');
        if (ta !== tb) return ta < tb ? -1 : 1;
        return 0;
      });
  }, [treatments, dateRangeBounds]);

  const filtered = useMemo(() => {
    if (selectedTypes.size === 0) return dateFiltered;
    return dateFiltered.filter(m => m.treatmentType && selectedTypes.has(m.treatmentType));
  }, [dateFiltered, selectedTypes]);

  const toggleType = (type) => {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const clearFilters = () => setSelectedTypes(new Set());

  const formatDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '—';
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const mDate = new Date(dt); mDate.setHours(0, 0, 0, 0);
    if (mDate.getTime() === today.getTime()) return t('student.today', { defaultValue: 'Today' });
    if (mDate.getTime() === tomorrow.getTime()) return t('student.tomorrow', { defaultValue: 'Tomorrow' });
    return dt.toLocaleDateString(i18n.language === 'ar' ? 'ar' : i18n.language === 'he' ? 'he' : 'en', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  };

  const formatTime = (time, endTime) => {
    if (!time) return '—';
    return endTime ? `${time} – ${endTime}` : time;
  };

  const fadeStyle = (side) => ({
    position: 'absolute',
    top: 0,
    [side]: 0,
    width: '24px',
    height: '100%',
    background: `linear-gradient(${side === (isRTL ? 'right' : 'left') ? (isRTL ? 'to left' : 'to right') : (isRTL ? 'to right' : 'to left')}, var(--color-surface, #FFFFFF), transparent)`,
    pointerEvents: 'none',
    zIndex: 1,
    transition: 'opacity 0.2s ease',
  });

  const allActive = selectedTypes.size === 0;

  return (
    <div style={{
      backgroundColor: 'var(--color-surface)',
      borderRadius: '12px',
      padding: isMobile ? '1rem' : '1.25rem 1.5rem',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
      border: '1px solid var(--color-border)',
      marginBottom: '1.25rem',
      direction: isRTL ? 'rtl' : 'ltr',
    }}>
      <style>{`.utt-scroll::-webkit-scrollbar { display: none; }`}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <Scissors size={18} color="var(--color-primary-dark, #355760)" />
        <h3 style={{
          fontSize: '1rem',
          fontWeight: 600,
          color: 'var(--color-text-primary)',
          margin: 0,
        }}>
          {t('dashboard.upcomingTreatments', { defaultValue: 'Upcoming Treatments' })}
        </h3>
        {filtered.length > 0 && (
          <span style={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'var(--color-primary-dark)',
            backgroundColor: 'var(--color-primary-light)',
            padding: '0.125rem 0.5rem',
            borderRadius: '10px',
            minWidth: '20px',
            textAlign: 'center',
          }}>
            {filtered.length}
          </span>
        )}
      </div>

      {/* Date-range pills */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '0.625rem',
        flexWrap: 'wrap',
      }}>
        {[
          { key: 'today', label: t('dashboard.rangeToday', { defaultValue: 'Today' }) },
          { key: 'next3', label: t('dashboard.rangeNext3Days', { defaultValue: 'Next 3 days' }) },
          { key: 'next7', label: t('dashboard.rangeNextWeek', { defaultValue: 'Next week' }) },
        ].map((opt) => (
          <FilterPill
            key={opt.key}
            label={opt.label}
            color="#355760"
            active={dateRange === opt.key}
            onClick={() => handleRangeChange(opt.key)}
            isMobile={isMobile}
          />
        ))}
      </div>

      {/* Filter badges */}
      <div style={{ position: 'relative', marginBottom: '0.875rem' }}>
        <div style={{ ...fadeStyle(isRTL ? 'right' : 'left'), opacity: showStartFade ? 1 : 0 }} />
        <div style={{ ...fadeStyle(isRTL ? 'left' : 'right'), opacity: showEndFade ? 1 : 0 }} />
        <div
          ref={scrollRef}
          className="utt-scroll"
          onScroll={updateFades}
          style={{
            display: 'flex',
            gap: '0.5rem',
            overflowX: 'auto',
            paddingBottom: '0.25rem',
            scrollSnapType: 'x mandatory',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
            direction: isRTL ? 'rtl' : 'ltr',
          }}
        >
          {/* All pill */}
          <FilterPill
            label={t('treatments.allTypes', { defaultValue: 'All types' })}
            color="#355760"
            active={allActive}
            onClick={clearFilters}
            isMobile={isMobile}
          />
          {treatmentTypes.map((type) => {
            const color = getTypeColor(type);
            return (
              <FilterPill
                key={type}
                label={type}
                color={color}
                active={selectedTypes.has(type)}
                onClick={() => toggleType(type)}
                isMobile={isMobile}
              />
            );
          })}
        </div>
      </div>

      {/* Content */}
      {treatments.length === 0 ? (
        <EmptyState isMobile={isMobile} t={t} />
      ) : dateFiltered.length === 0 ? (
        <EmptyState isMobile={isMobile} t={t} dateRange={dateRange} />
      ) : filtered.length === 0 ? (
        <NoResults t={t} onClear={clearFilters} />
      ) : isMobile ? (
        <MobileCards
          list={filtered}
          clientMap={clientMap}
          workerMap={workerMap}
          onRowClick={onRowClick}
          onRemove={onRemove}
          onUpdateStatus={onUpdateStatus}
          formatDate={formatDate}
          formatTime={formatTime}
          t={t}
        />
      ) : (
        <DesktopTable
          list={filtered}
          isTablet={isTablet}
          clientMap={clientMap}
          workerMap={workerMap}
          onRowClick={onRowClick}
          onRemove={onRemove}
          onUpdateStatus={onUpdateStatus}
          formatDate={formatDate}
          formatTime={formatTime}
          t={t}
        />
      )}
    </div>
  );
}

function FilterPill({ label, color, active, onClick, isMobile }) {
  const bg = active ? color : hexToRgba(color, 0.1);
  const fg = active ? '#fff' : color;
  const ring = active ? `0 0 0 2px ${hexToRgba(color, 0.2)}` : 'none';
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.filter = 'brightness(0.92)';
        e.currentTarget.style.transform = 'scale(1.03)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.filter = 'brightness(1)';
        e.currentTarget.style.transform = 'scale(1)';
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.375rem',
        padding: isMobile ? '0.4375rem 0.75rem' : '0.5rem 0.875rem',
        borderRadius: 9999,
        border: 'none',
        backgroundColor: bg,
        color: fg,
        boxShadow: ring,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        scrollSnapAlign: 'start',
        flexShrink: 0,
        minHeight: isMobile ? 36 : undefined,
        fontSize: '0.8125rem',
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {active && <Check size={14} strokeWidth={3} style={{ flexShrink: 0 }} />}
      {label}
    </button>
  );
}

function EmptyState({ isMobile, t, dateRange }) {
  const subKey = dateRange === 'today'
    ? 'dashboard.noUpcomingTreatmentsToday'
    : dateRange === 'next3'
      ? 'dashboard.noUpcomingTreatments3d'
      : 'dashboard.noUpcomingTreatments7d';
  const subDefault = dateRange === 'today'
    ? 'No treatments today'
    : dateRange === 'next3'
      ? 'No treatments in the next 3 days'
      : 'No treatments in the next 7 days';
  return (
    <div style={{
      textAlign: 'center',
      padding: isMobile ? '2rem 1rem' : '2.5rem 1.5rem',
      color: 'var(--color-text-secondary)',
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: '50%',
        background: 'rgba(139, 92, 246, 0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 0.75rem',
      }}>
        <Scissors size={24} style={{ color: '#7C3AED', opacity: 0.6 }} />
      </div>
      <p style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 600, color: 'var(--color-text-primary)' }}>
        {t('treatments.noTreatmentsYet', { defaultValue: 'No treatments scheduled yet' })}
      </p>
      <p style={{ margin: '0.375rem 0 0 0', fontSize: '0.8125rem' }}>
        {t(subKey, { defaultValue: subDefault })}
      </p>
    </div>
  );
}

function NoResults({ t, onClear }) {
  return (
    <div style={{
      textAlign: 'center',
      padding: '2rem 1.5rem',
      color: 'var(--color-text-secondary)',
    }}>
      <p style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 600, color: 'var(--color-text-primary)' }}>
        {t('common.noResults', { defaultValue: 'No results found' })}
      </p>
      <p style={{ margin: '0.375rem 0 1rem 0', fontSize: '0.8125rem' }}>
        {t('common.tryDifferentFilters', { defaultValue: 'Try adjusting your filters' })}
      </p>
      <button
        type="button"
        onClick={onClear}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', background: 'transparent',
          color: 'var(--color-text-primary)',
          border: '1px solid var(--color-border)', borderRadius: 8,
          fontWeight: 500, fontSize: '0.8125rem', cursor: 'pointer',
        }}
      >
        <RotateCcw size={14} />
        {t('common.clearFilters', { defaultValue: 'Clear Filters' })}
      </button>
    </div>
  );
}

// Cancelled rows are visually distinct so the eye skips them at a glance:
// soft red tint + faded text + line-through on the primary content.
const CANCELLED_BG = 'rgba(239, 68, 68, 0.06)';
const CANCELLED_BG_HOVER = 'rgba(239, 68, 68, 0.12)';
const CANCELLED_ACCENT = '#ef4444';

function isCancelled(m) {
  return (m && m.status) === 'cancelled';
}

function DesktopTable({ list, isTablet, clientMap, workerMap, onRowClick, onRemove, onUpdateStatus, formatDate, formatTime, t }) {
  const actionColCount = (onUpdateStatus ? 1 : 0) + (onRemove ? 1 : 0);
  return (
    <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid var(--color-border)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
        <thead>
          <tr style={{ background: 'var(--color-hover, #F9FAFB)' }}>
            <th style={thStyle}>{t('common.date', { defaultValue: 'Date' })}</th>
            <th style={thStyle}>{t('common.time', { defaultValue: 'Time' })}</th>
            <th style={thStyle}>{t('common.client', { defaultValue: 'Client' })}</th>
            {!isTablet && <th style={thStyle}>{t('treatments.filterBySpecialist', { defaultValue: 'Specialist' })}</th>}
            <th style={thStyle}>{t('treatments.filterByType', { defaultValue: 'Type' })}</th>
            <th style={thStyle}>{t('common.status', { defaultValue: 'Status' })}</th>
            {actionColCount > 0 && (
              <th style={{ ...thStyle, width: 32 * actionColCount + 8 }} aria-label={t('common.actions', { defaultValue: 'Actions' })} />
            )}
          </tr>
        </thead>
        <tbody>
          {list.map((m, idx) => {
            const cancelled = isCancelled(m);
            const baseBg = cancelled ? CANCELLED_BG : 'transparent';
            const hoverBg = cancelled ? CANCELLED_BG_HOVER : 'var(--color-hover, #F9FAFB)';
            const cancelledTextStyle = cancelled
              ? { textDecoration: 'line-through', textDecorationColor: CANCELLED_ACCENT, opacity: 0.65 }
              : null;
            return (
              <tr
                key={m.id}
                style={{
                  borderTop: idx > 0 ? '1px solid var(--color-border)' : 'none',
                  background: baseBg,
                  transition: 'background 0.12s',
                  cursor: onRowClick ? 'pointer' : 'default',
                  ...(cancelled
                    ? { boxShadow: `inset 3px 0 0 ${CANCELLED_ACCENT}` }
                    : null),
                }}
                onClick={() => onRowClick?.(m)}
                onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = baseBg; }}
              >
                <td style={tdStyle}>
                  <span style={{ fontWeight: 500, ...cancelledTextStyle }}>{formatDate(m.date)}</span>
                </td>
                <td style={tdStyle}>
                  <span style={{ color: 'var(--color-text-secondary)', fontVariantNumeric: 'tabular-nums', ...cancelledTextStyle }}>
                    {formatTime(m.time, m.endTime)}
                  </span>
                </td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      width: 26, height: 26, borderRadius: '50%',
                      background: cancelled ? 'rgba(239, 68, 68, 0.12)' : 'var(--color-primary-light, rgba(53,87,96,0.08))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <User size={13} color={cancelled ? CANCELLED_ACCENT : 'var(--color-primary-dark, #355760)'} />
                    </div>
                    <span style={{ fontWeight: 600, color: 'var(--color-text-primary)', ...cancelledTextStyle }}>
                      {clientMap[m.studentId] || clientMap[m.transplantClientId] || m.studentName || m.clientName || '—'}
                    </span>
                  </div>
                </td>
                {!isTablet && (
                  <td style={tdStyle}>
                    <span style={{ color: 'var(--color-text-secondary)', ...cancelledTextStyle }}>
                      {workerMap[m.teacherId] || '—'}
                    </span>
                  </td>
                )}
                <td style={tdStyle}>
                  {m.treatmentType ? (
                    <span style={{ ...typeBadge(getTypeColor(m.treatmentType)), ...(cancelled ? { opacity: 0.55 } : null) }}>
                      {m.treatmentType}
                    </span>
                  ) : '—'}
                </td>
                <td style={tdStyle}>
                  <TreatmentStatusBadge status={m.status} date={m.date} time={m.time} endTime={m.endTime} />
                </td>
                {actionColCount > 0 && (
                  <td style={{ ...tdStyle, padding: '6px 8px' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {onUpdateStatus && (
                        <UpdateStatusButton onClick={(e) => { e.stopPropagation(); onUpdateStatus(m); }} t={t} />
                      )}
                      {onRemove && (
                        <RemoveButton onClick={(e) => { e.stopPropagation(); onRemove(m); }} t={t} />
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RemoveButton({ onClick, t }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={t('common.remove', { defaultValue: 'Remove' })}
      aria-label={t('common.remove', { defaultValue: 'Remove' })}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.12)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        minHeight: 0,
        padding: 0,
        border: 'none',
        borderRadius: 6,
        background: 'transparent',
        color: '#ef4444',
        cursor: 'pointer',
        transition: 'background 0.15s ease',
        flexShrink: 0,
      }}
    >
      <Trash2 size={15} />
    </button>
  );
}

function UpdateStatusButton({ onClick, t }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={t('treatments.updateStatus', { defaultValue: 'Update status' })}
      aria-label={t('treatments.updateStatus', { defaultValue: 'Update status' })}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(53, 87, 96, 0.12)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        minHeight: 0,
        padding: 0,
        border: 'none',
        borderRadius: 6,
        background: 'transparent',
        color: 'var(--color-primary-dark, #355760)',
        cursor: 'pointer',
        transition: 'background 0.15s ease',
        flexShrink: 0,
      }}
    >
      <Edit3 size={14} />
    </button>
  );
}

function MobileCards({ list, clientMap, workerMap, onRowClick, onRemove, onUpdateStatus, formatDate, formatTime, t }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {list.map((m) => {
        const cancelled = isCancelled(m);
        const cardBg = cancelled ? CANCELLED_BG : 'var(--color-surface)';
        const cardBgHover = cancelled ? CANCELLED_BG_HOVER : 'var(--color-hover, #F9FAFB)';
        const cardBorder = cancelled ? CANCELLED_ACCENT : 'var(--color-border)';
        const strike = cancelled
          ? { textDecoration: 'line-through', textDecorationColor: CANCELLED_ACCENT, opacity: 0.7 }
          : null;
        return (
          <div
            key={m.id}
            onClick={() => onRowClick?.(m)}
            style={{
              padding: '12px 14px',
              border: `1px solid ${cardBorder}`,
              borderInlineStart: cancelled ? `4px solid ${CANCELLED_ACCENT}` : `1px solid ${cardBorder}`,
              borderRadius: 10,
              cursor: onRowClick ? 'pointer' : 'default',
              background: cardBg,
            }}
            onTouchStart={(e) => { e.currentTarget.style.background = cardBgHover; }}
            onTouchEnd={(e) => { e.currentTarget.style.background = cardBg; }}
          >
            {/* Row 1: date/time + status (actions move to their own row below) */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <CalendarIcon size={13} color="var(--color-text-secondary)" />
                <span style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--color-text-secondary)', ...strike }}>
                  {formatDate(m.date)}
                </span>
                <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>·</span>
                <Clock size={13} color="var(--color-text-secondary)" />
                <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', fontVariantNumeric: 'tabular-nums', ...strike }}>
                  {formatTime(m.time, m.endTime)}
                </span>
              </div>
              <div style={{ flexShrink: 0 }}>
                <TreatmentStatusBadge status={m.status} date={m.date} time={m.time} endTime={m.endTime} />
              </div>
            </div>

            {/* Row 2: client */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <div style={{
                width: 30, height: 30, borderRadius: '50%',
                background: cancelled ? 'rgba(239, 68, 68, 0.12)' : 'var(--color-primary-light, rgba(53,87,96,0.08))',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <User size={14} color={cancelled ? CANCELLED_ACCENT : 'var(--color-primary-dark, #355760)'} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...strike }}>
                  {clientMap[m.studentId] || clientMap[m.transplantClientId] || m.studentName || m.clientName || '—'}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2, flexWrap: 'wrap' }}>
                  {m.treatmentType && (
                    <span style={{ ...typeBadge(getTypeColor(m.treatmentType)), fontSize: '0.6875rem', ...(cancelled ? { opacity: 0.6 } : null) }}>
                      {m.treatmentType}
                    </span>
                  )}
                  {workerMap[m.teacherId] && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                      {workerMap[m.teacherId]}
                    </span>
                  )}
                </div>
              </div>
            </div>

          {m.place && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>
              <MapPin size={12} />
              {m.place}
            </div>
          )}

          {(onUpdateStatus || onRemove) && (
            <div style={{
              display: 'flex',
              gap: 8,
              marginTop: 10,
              paddingTop: 8,
              borderTop: '1px solid var(--color-border)',
            }}>
              {onUpdateStatus && (
                <MobileActionButton
                  icon={Edit3}
                  label={t('treatments.updateStatus', { defaultValue: 'Update status' })}
                  onClick={(e) => { e.stopPropagation(); onUpdateStatus(m); }}
                  tone="primary"
                />
              )}
              {onRemove && (
                <MobileActionButton
                  icon={Trash2}
                  label={t('common.remove', { defaultValue: 'Remove' })}
                  onClick={(e) => { e.stopPropagation(); onRemove(m); }}
                  tone="danger"
                />
              )}
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
}

function MobileActionButton({ icon: Icon, label, onClick, tone }) {
  const isDanger = tone === 'danger';
  const color = isDanger ? '#ef4444' : 'var(--color-primary-dark, #355760)';
  const bg = isDanger ? 'rgba(239, 68, 68, 0.08)' : 'rgba(53, 87, 96, 0.08)';
  return (
    <button
      type="button"
      onClick={onClick}
      onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(0.97)'; }}
      onTouchEnd={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      style={{
        flex: 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        minHeight: 40,
        padding: '8px 12px',
        border: 'none',
        borderRadius: 8,
        background: bg,
        color,
        fontSize: '0.8125rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'transform 0.12s ease',
      }}
    >
      <Icon size={15} />
      {label}
    </button>
  );
}

const thStyle = {
  padding: '10px 14px',
  fontWeight: 600, fontSize: '0.6875rem', letterSpacing: '0.04em',
  color: 'var(--color-text-secondary)',
  textAlign: 'start', textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid var(--color-border)',
};

const tdStyle = {
  padding: '10px 14px',
  color: 'var(--color-text-primary)',
  whiteSpace: 'nowrap',
  fontSize: '0.8125rem',
};

function typeBadge(color) {
  return {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    background: hexToRgba(color, 0.1),
    color,
  };
}
